import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AIWorker, ChatMessage, GitLabProject } from '../types';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface Props {
  workers: AIWorker[];
}

export const WorkerChat: React.FC<Props> = ({ workers }) => {
  const [selectedWorkerId, setSelectedWorkerId] = useState<string>(workers[0]?.id || '');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState<'chat' | 'task'>('chat');
  const [projects, setProjects] = useState<GitLabProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const selectedWorker = workers.find((w) => w.id === selectedWorkerId);

  // Load messages on worker change
  useEffect(() => {
    if (!selectedWorkerId) return;
    fetch(`${API}/api/workers/${selectedWorkerId}/messages`)
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setMessages(data); })
      .catch(() => setMessages([]));
  }, [selectedWorkerId]);

  // Load projects for task mode
  useEffect(() => {
    fetch(`${API}/api/gitlab/projects`)
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setProjects(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    setSending(true);

    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      workerId: selectedWorkerId,
      sender: 'user',
      message: input,
      timestamp: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages((prev) => [...prev, userMsg]);
    const msgText = input;
    setInput('');

    try {
      if (mode === 'task') {
        // Create task directly
        const res = await fetch(`${API}/api/workers/${selectedWorkerId}/task`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: msgText,
            projectId: selectedProjectId || undefined,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setMessages((prev) => [...prev, {
            id: `msg-${Date.now()}-task`,
            workerId: selectedWorkerId,
            sender: 'worker',
            message: `Задача создана (${data.taskId?.substring(0, 8)}). Начинаю работу.`,
            timestamp: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
          }]);
        } else {
          const err = await res.json();
          setMessages((prev) => [...prev, {
            id: `msg-${Date.now()}-err`,
            workerId: selectedWorkerId,
            sender: 'worker',
            message: `Ошибка: ${err.error || 'не удалось создать задачу'}`,
            timestamp: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
          }]);
        }
      } else {
        // Regular chat message (context only)
        const res = await fetch(`${API}/api/workers/${selectedWorkerId}/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: msgText }),
        });
        if (res.ok) {
          const reply = await res.json();
          if (reply.message) {
            setMessages((prev) => [...prev, {
              id: `msg-${Date.now()}-reply`,
              workerId: selectedWorkerId,
              sender: 'worker',
              message: reply.message,
              timestamp: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
            }]);
          }
        }
      }
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: `msg-${Date.now()}-err`,
        workerId: selectedWorkerId,
        sender: 'worker',
        message: 'Не удалось отправить. Проверьте подключение к backend.',
        timestamp: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
      }]);
    }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="worker-chat">
      {/* Worker selector */}
      <div className="chat-worker-tabs">
        {workers.map((w) => (
          <button
            key={w.id}
            className={`chat-worker-tab ${selectedWorkerId === w.id ? 'chat-tab-active' : ''}`}
            onClick={() => setSelectedWorkerId(w.id)}
          >
            <span className="chat-tab-avatar">{w.avatar}</span>
            <span>{w.name}</span>
          </button>
        ))}
      </div>

      {/* Chat header */}
      {selectedWorker && (
        <div className="chat-header">
          <div>
            <h3>{selectedWorker.avatar} {selectedWorker.name}</h3>
            <span className="chat-worker-role">{selectedWorker.description}</span>
          </div>
          <span className={`chat-status ${selectedWorker.status === 'working' ? 'chat-online' : ''}`}>
            {selectedWorker.status}
          </span>
        </div>
      )}

      {/* Messages */}
      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty">
            {mode === 'chat'
              ? 'Напишите сообщение для контекста или переключитесь в режим задачи'
              : 'Опишите задачу — она будет создана и выполнена выбранным работником'}
          </div>
        )}
        {messages.map((msg) => (
          <motion.div
            key={msg.id}
            className={`chat-msg ${msg.sender === 'user' ? 'chat-msg-user' : 'chat-msg-worker'}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="chat-msg-content">{msg.message}</div>
            <span className="chat-msg-time">{msg.timestamp}</span>
          </motion.div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Mode + Project selector */}
      <div className="chat-controls">
        <div className="chat-mode-toggle">
          <button
            className={`mode-btn ${mode === 'chat' ? 'mode-active' : ''}`}
            onClick={() => setMode('chat')}
          >
            Контекст
          </button>
          <button
            className={`mode-btn ${mode === 'task' ? 'mode-active mode-task' : ''}`}
            onClick={() => setMode('task')}
          >
            Задача
          </button>
        </div>
        {mode === 'task' && projects.length > 0 && (
          <select
            className="chat-project-select"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            <option value="">Без проекта</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Input */}
      <div className="chat-input-area">
        <textarea
          className="chat-input"
          placeholder={mode === 'task'
            ? 'Опишите задачу... (Enter для запуска)'
            : 'Контекст для следующей задачи... (Enter)'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
        />
        <button
          className="btn-primary chat-send"
          onClick={sendMessage}
          disabled={sending || !input.trim()}
          style={mode === 'task' ? { background: '#22c55e' } : undefined}
        >
          {sending ? '...' : mode === 'task' ? 'Run' : 'Send'}
        </button>
      </div>
    </div>
  );
};
